import './utils/polyfills';
import { installChunkErrorRecovery } from 'lib/chunkErrorRecovery';
import { captureNativeNetwork } from 'lib/nativeNetwork';
import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { router } from './routes';
import { ThemeProvider } from 'providers/theme';
import { EasyPeasyProvider } from 'providers/easyPeasy';
import { I18nProvider } from 'providers/i18n';
import { ReactQueryProvider } from 'providers/reactQuery';
import { NotificationProvider } from 'providers/notification';
import { IconProvider } from 'providers/icon';
import { AuthProvider } from 'providers/auth';
import { CssBaseline } from '@mui/material';
import { GlobalStyles } from 'styles/globalStyles';
import { TaskBar } from 'features/modalManager/components/TaskBar';
import { mockServer } from 'mocks/browser';

// Must be installed before anything can lazy-import: recovers from chunk-load failures
// (mid-deploy 404s and cache-poisoned assets) instead of stranding the user on the raw
// "Unexpected Application Error!" page (DL-61837). Imports are hoisted, so calling here
// still runs before any dynamic import() can fire.
installChunkErrorRecovery();

// Deployed bundle version, inspectable via `window.APP_VERSION` in a live console.
// Same constant that busts the persisted query cache (lib/react-query/indexedDB).
window.APP_VERSION = APP_VERSION;

// if you run the frontend over ip, then crypto is not available, so we need to polyfill it
if (!crypto.randomUUID && crypto.getRandomValues) {
  crypto.randomUUID = (): `${string}-${string}-${string}-${string}-${string}` => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = crypto.getRandomValues(new Uint8Array(1))[0] & 15;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    }) as `${string}-${string}-${string}-${string}-${string}`;
  };
}

captureNativeNetwork();
mockServer();

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <StrictMode>
    <I18nProvider>
      <ReactQueryProvider>
        <EasyPeasyProvider>
          <ThemeProvider>
            <IconProvider>
              <CssBaseline />
              <GlobalStyles />
              <AuthProvider>
                <TaskBar />
                <NotificationProvider />
                <RouterProvider router={router} />
              </AuthProvider>
            </IconProvider>
          </ThemeProvider>
        </EasyPeasyProvider>
      </ReactQueryProvider>
    </I18nProvider>
  </StrictMode>
);
